import * as DadosMoveis from "@/platform/tirvu-rede";
import Api from "@/services/Api";
import ApiPrestador, {
  escopoPrestador,
  identidadePrestador,
  peekPrestadorSessionGen,
} from "@/services/ApiPrestador";
import { SyncService } from "@/services/Sync";
import AsyncStorage from "@/platform/async-storage";
import * as FileSystem from "@/platform/file-system";
import {
  caminhoNoAppAtual,
  reescreverCaminhosAntigos,
} from "@/services/CaminhoArquivo";

/**
 * DIÁRIA DO PRESTADOR — registro offline-first.
 *
 * REGRA DE OURO (a mesma do ponto do CLT): JAMAIS perder um registro, e NADA pode
 * travar quem está registrando. O toque confirma na hora contra o espelho LOCAL; o
 * envio ao servidor roda depois, em background, e pode falhar quantas vezes quiser.
 *
 * ⚠️ UMA estrutura só: o espelho É a fila (cada diária carrega `enviado`). No
 * colaborador existem duas (mapa `local_pontos` × fila de ações) e foi exatamente a
 * divergência entre elas que criou os pontos "órfãos" que nunca subiam. Aqui não há
 * o que divergir.
 *
 * ⚠️ ESCOPO = TENANT + PESSOA (`...#HASH:ID`), sempre do CLAIM DO TOKEN.
 *
 * Escopar só por tenant foi um bug GRAVE (2026-08-14): dois CPFs da MESMA empresa
 * dividiam o espelho e quem logava depois via a diária do anterior. Tenant
 * identifica a EMPRESA, não QUEM está logado — e o mesmo prestador atende várias
 * empresas, cujos ids colidem entre bancos. Precisa dos dois.
 */

const PREFIXO_DIARIAS = "@tirvumais:prestador_diarias";
const DIR_FOTOS = `${(FileSystem as any).documentDirectory}prestador_diarias/`;

export type DiariaLocal = {
  /** Chave local (nunca vem do servidor) — identifica a diária no espelho. */
  localId: string;
  /** Tenant em que a diária foi registrada. Guarda cross-tenant no envio. */
  tenantHash: string;
  /** CPF só dígitos — é o que vai em tab_rh_pontos.id_adm_colaborador. */
  cpf: string;
  /** id_adm_colaborador_freelancer (auditoria; o ponto NÃO usa este id). */
  idPrestador: number;
  /** Posto escolhido (id_adm_cliente REAL). */
  idAdmCliente: number;
  idAdmEmpresa: number;
  postoNome: string;
  /** DD/MM/YYYY — dia da diária. */
  dataCompetencia: string;
  /** "YYYY-MM-DD HH:mm:ss" LOCAL, carimbado no momento do registro. */
  dataLog: string;
  /** HH:MM para exibir no botão. */
  hora: string;
  latitude: string | null;
  longitude: string | null;
  latitudeLotacao: string | null;
  longitudeLotacao: string | null;
  /** Distância (km) entre o GPS e o posto. */
  distancia: string | null;
  expressao: string | null;
  /** Arquivo local da selfie (persistido; apagado só depois do 200). */
  fotoUri: string | null;
  /** true = o T-ARC aceitou o envio (200 = ENFILEIRADO, ver enviadoEm). */
  enviado: boolean;
  /**
   * Quando o envio foi aceito (epoch ms).
   *
   * ⚠️ Existe por causa de um detalhe que já custou caro no projeto: **200 do T-ARC
   * significa ENFILEIRADO no RabbitMQ, não gravado no MySQL**. Entre o 200 e o
   * INSERT do T-Consumer existe uma janela em que o servidor legitimamente ainda
   * NÃO tem a diária — e a reconciliação não pode confundir isso com "excluída".
   */
  enviadoEm?: number;
  tentativas: number;
  ultimoErro?: string;
  /**
   * URL pública da selfie, devolvida pelo T-ARC no envio (o storage já tem a foto). Um
   * REENVIO (diária fantasma, ver reconciliarDiariaDoDia) manda o LINK: o arquivo local é
   * apagado depois do envio, e o T-ARC repassa link sem subir de novo.
   */
  fotoUrl?: string;
  /** Quando a diária foi vista NO BANCO (consulta do dia). Legado: `enviadoEm === 1`. */
  confirmadaEm?: number;
  /** Recusas SEGUIDAS do servidor (4xx de negócio) — dão a espera abaixo. */
  recusas?: number;
  /** Epoch ms: antes disto o envio AUTOMÁTICO não tenta (recusa do servidor). */
  proximaTentativaEm?: number;
  /** Reenvios de diária fantasma (o T-ARC aceitou, o banco não tem) — teto anti-loop. */
  curas?: number;
  ultimaCuraEm?: number;
};

const normalizarHash = (hash: unknown) =>
  String(hash ?? "")
    .trim()
    .toUpperCase();

/** Chave do espelho: `@tirvumais:prestador_diarias#HASH:ID`. */
const chaveDiarias = (escopo: string) => `${PREFIXO_DIARIAS}#${escopo}`;

/** Chave LEGADA (só tenant) — existiu antes do escopo por pessoa. */
const chaveLegadaTenant = (hash: string) =>
  `${PREFIXO_DIARIAS}#${normalizarHash(hash)}`;

/**
 * MIGRAÇÃO única do espelho legado (escopo só por tenant) para o escopo por pessoa.
 *
 * Roda antes de qualquer leitura/escrita e move APENAS as diárias desta pessoa
 * (por id, ou por CPF quando o id não foi gravado). As dos outros ficam onde estão
 * — quando aquele prestador logar, ele migra as suas. Nada é descartado: uma diária
 * pendente presa numa chave invisível seria registro PERDIDO.
 */
let _migracaoFeita = new Set<string>();

const migrarLegado = async (escopo: string): Promise<void> => {
  if (_migracaoFeita.has(escopo)) return;
  _migracaoFeita.add(escopo);
  try {
    const { hash, idPrestador, cpf } = await identidadePrestador();
    if (!hash) return;
    const chaveVelha = chaveLegadaTenant(hash);
    const chaveNova = chaveDiarias(escopo);
    if (chaveVelha === chaveNova) return;

    const legado = await lerLista(chaveVelha);
    if (legado.length === 0) return;

    const minhas = legado.filter(
      (d) =>
        (idPrestador > 0 && Number(d.idPrestador) === idPrestador) ||
        (!!cpf && String(d.cpf ?? "").replace(/\D/g, "") === cpf),
    );
    const dosOutros = legado.filter((d) => !minhas.includes(d));

    if (minhas.length > 0) {
      const atuais = await lerLista(chaveNova);
      const vistos = new Set(atuais.map((d) => d.localId));
      await gravarLista(chaveNova, [
        ...atuais,
        ...minhas.filter((d) => !vistos.has(d.localId)),
      ]);
    }

    if (dosOutros.length > 0) await gravarLista(chaveVelha, dosOutros);
    else await AsyncStorage.removeItem(chaveVelha);
  } catch {
    // Migração é best-effort: falhar aqui não pode impedir o registro.
  }
};

/** Carimbo LOCAL "YYYY-MM-DD HH:mm:ss" — NUNCA toISOString() (que é UTC = BRT+3).
 *  O backend grava o data_log VERBATIM, então tem que ser a hora do evento. */
export const agoraSqlLocal = (d: Date = new Date()): string => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

/** DD/MM/YYYY do dia — a "competência" da diária é simplesmente o dia. */
export const competenciaDeHoje = (d: Date = new Date()): string => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
};

// ── Espelho por tenant, com leitura-modificação-escrita SERIALIZADA ────────────
// Sem serialização, dois fluxos (registro + dreno) leem a mesma lista e o último a
// gravar apaga a alteração do outro — o jeito clássico de "perder" uma diária.
const filaRmw = new Map<string, Promise<unknown>>();

const serializar = <T,>(chave: string, fn: () => Promise<T>): Promise<T> => {
  const anterior = filaRmw.get(chave) ?? Promise.resolve();
  const proxima = anterior.then(fn, fn);
  // A cadeia não pode morrer por causa de uma rejeição.
  filaRmw.set(
    chave,
    proxima.catch(() => undefined),
  );
  return proxima;
};

const lerLista = async (chave: string): Promise<DiariaLocal[]> => {
  try {
    const bruto = await AsyncStorage.getItem(chave);
    if (!bruto) return [];
    const parsed = reescreverCaminhosAntigos(JSON.parse(bruto));
    return Array.isArray(parsed) ? parsed.filter((d) => d && d.localId) : [];
  } catch {
    return [];
  }
};

/**
 * Leitura para quem vai REGRAVAR a lista. O `lerLista` devolve `[]` em qualquer erro —
 * certo para exibir, fatal para gravar: a escrita seguinte trocaria o espelho por uma
 * lista só com a alteração e apagaria as diárias pendentes de outros dias. Aqui:
 *  - falha de LEITURA (storage indisponível) → lança; quem grava desiste e nada muda;
 *  - valor que não é lista → quarentena (cópia forense) e recomeça vazio, para o espelho
 *    não ficar travado para sempre.
 */
const lerListaParaEscrita = async (chave: string): Promise<DiariaLocal[]> => {
  const bruto = await AsyncStorage.getItem(chave);
  if (!bruto) return [];
  try {
    const parsed = reescreverCaminhosAntigos(JSON.parse(bruto));
    if (Array.isArray(parsed)) return parsed.filter((d) => d && d.localId);
  } catch {}
  console.error(
    "[PrestadorDiaria] ESPELHO ILEGÍVEL — quarentenando e recomeçando vazio.",
  );
  try {
    await AsyncStorage.setItem(`${chave}_corrupt`, bruto.slice(0, 512 * 1024));
  } catch {}
  return [];
};

// ── Aviso de mudança no espelho ─────────────────────────────────────────────────
// A tela de Registro de Diária lê o espelho para mostrar a diária do dia. O envio roda em
// SEGUNDO PLANO (o dreno que a câmera dispara, o agendador, a tarefa do sistema) e muda o
// espelho depois dessa leitura — sem aviso, a tela ficava com o estado de quando a câmera
// voltou. Toda gravação passa por `gravarLista`, que avisa quem observa; o aviso é
// agrupado (200 ms), porque um dreno grava várias vezes seguidas.
const ouvintesDiarias = new Set<() => void>();
let avisoDiariasAgendado: ReturnType<typeof setTimeout> | null = null;
const avisarDiariasMudaram = () => {
  if (ouvintesDiarias.size === 0 || avisoDiariasAgendado) return;
  avisoDiariasAgendado = setTimeout(() => {
    avisoDiariasAgendado = null;
    for (const ouvinte of [...ouvintesDiarias]) {
      try {
        ouvinte();
      } catch {}
    }
  }, 200);
};

/** Avisa quando o espelho de diárias muda (registrada, enviada, confirmada, reenviada). */
export const observarDiarias = (ouvinte: () => void): (() => void) => {
  ouvintesDiarias.add(ouvinte);
  return () => {
    ouvintesDiarias.delete(ouvinte);
  };
};

const gravarLista = async (chave: string, lista: DiariaLocal[]) => {
  await AsyncStorage.setItem(chave, JSON.stringify(lista));
  avisarDiariasMudaram();
};

/**
 * Leitura-modificação-escrita atômica do espelho de UM tenant.
 * A chave é resolvida UMA vez e a mesma é usada para ler e gravar — resolver de
 * novo na escrita é como uma troca de empresa no meio grava no tenant errado.
 */
const mutarDiarias = <T,>(
  hash: string,
  mutador: (lista: DiariaLocal[]) => { lista: DiariaLocal[]; retorno: T },
): Promise<T> => {
  const chave = chaveDiarias(hash);
  return serializar(chave, async () => {
    const atual = await lerListaParaEscrita(chave);
    const { lista, retorno } = mutador(atual);
    await gravarLista(chave, lista);
    return retorno;
  });
};

/**
 * Diárias desta PESSOA neste TENANT. Sem argumento de propósito: a identidade sai
 * SEMPRE do token, então nenhum chamador consegue passar o escopo errado.
 */
export const lerDiariasLocais = async (): Promise<DiariaLocal[]> => {
  const escopo = await escopoPrestador();
  if (!escopo) return []; // fail-closed: sem sessão identificada, sem diárias
  await migrarLegado(escopo);
  return lerLista(chaveDiarias(escopo));
};

/** Diária JÁ REGISTRADA no dia (o botão vira "registrada" com esta hora). */
export const diariaDoDia = async (
  dataCompetencia: string,
): Promise<DiariaLocal | null> => {
  const lista = await lerDiariasLocais();
  return (
    lista.find((d) => d.dataCompetencia === dataCompetencia) ?? null
  );
};

// ── Foto ──────────────────────────────────────────────────────────────────────

const garantirDirFotos = async () => {
  const info = await FileSystem.getInfoAsync(DIR_FOTOS);
  if (!info.exists)
    await FileSystem.makeDirectoryAsync(DIR_FOTOS, { intermediates: true });
};

/**
 * Copia a selfie da câmera (arquivo temporário, que o SO pode limpar) para um
 * diretório persistente. Sem isto, uma diária registrada offline chegaria ao
 * servidor sem foto quando o cache fosse limpo.
 */
export const persistirFotoDiaria = async (
  uri: string,
): Promise<string | null> => {
  try {
    await garantirDirFotos();
    const nome = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}.jpg`;
    const destino = `${DIR_FOTOS}${nome}`;
    await FileSystem.copyAsync({ from: uri, to: destino });
    return destino;
  } catch {
    // Falhou em persistir: segue com o URI original (melhor tentar com a foto
    // temporária do que perder a diária).
    return null;
  }
};

const DIR_PERFIL = `${(FileSystem as any).documentDirectory}prestador_perfil/`;

/**
 * Cópia PERSISTENTE da selfie para servir de foto de PERFIL enquanto o servidor não
 * devolve a URL.
 *
 * Arquivo SEPARADO do da fila de propósito: o da fila é apagado quando a diária
 * sobe, e um perfil apontando para arquivo apagado viraria imagem quebrada.
 */
export const persistirFotoPerfil = async (
  uri: string,
): Promise<string | null> => {
  try {
    const info = await FileSystem.getInfoAsync(DIR_PERFIL);
    if (!info.exists)
      await FileSystem.makeDirectoryAsync(DIR_PERFIL, { intermediates: true });
    const destino = `${DIR_PERFIL}perfil_${Date.now()}.jpg`;
    await FileSystem.copyAsync({ from: uri, to: destino });
    return destino;
  } catch {
    return null;
  }
};

/** Remove a cópia local do perfil (chamado ao trocar por uma URL do servidor). */
export const apagarFotoPerfilLocal = async (uri: string | null | undefined) => {
  const u = String(uri ?? "");
  if (!u.startsWith(DIR_PERFIL)) return; // nunca apaga foto que não é nossa
  try {
    await FileSystem.deleteAsync(u, { idempotent: true });
  } catch {
    /* ignore */
  }
};

const apagarFoto = async (uri: string | null) => {
  if (!uri) return;
  try {
    await FileSystem.deleteAsync(uri, { idempotent: true });
  } catch {
    /* arquivo já sumiu: nada a fazer */
  }
};

// ── Registro (otimista) ───────────────────────────────────────────────────────

export type NovaDiaria = {
  cpf: string;
  idPrestador: number;
  idAdmCliente: number;
  idAdmEmpresa: number;
  postoNome: string;
  latitude: string | null;
  longitude: string | null;
  latitudeLotacao: string | null;
  longitudeLotacao: string | null;
  distancia: string | null;
  expressao: string | null;
  /** URI da selfie (temporária da câmera). É persistida aqui. */
  fotoUri: string | null;
};

/**
 * Grava a diária LOCALMENTE e confirma na hora. O envio é responsabilidade do
 * dreno — o usuário não espera rede para nada.
 *
 * Idempotência de PRODUTO: uma diária por dia. Se já existir uma no dia, devolve
 * a que existe em vez de criar outra (dois toques rápidos não geram dois
 * registros, e o UNIQUE do banco não é a última linha de defesa).
 */
export const registrarDiariaLocal = async (
  dados: NovaDiaria,
): Promise<{ diaria: DiariaLocal; jaExistia: boolean } | null> => {
  const escopo = await escopoPrestador();
  if (!escopo) return null;
  await migrarLegado(escopo);
  const { hash: h, idPrestador: idSessao } = await identidadePrestador();
  if (!h) return null;

  const agora = new Date();
  const dataCompetencia = competenciaDeHoje(agora);
  const p = (n: number) => String(n).padStart(2, "0");

  const fotoPersistida = dados.fotoUri
    ? ((await persistirFotoDiaria(dados.fotoUri)) ?? dados.fotoUri)
    : null;

  return mutarDiarias<{ diaria: DiariaLocal; jaExistia: boolean }>(
    escopo,
    (lista) => {
    const existente = lista.find((d) => d.dataCompetencia === dataCompetencia);
    if (existente) return { lista, retorno: { diaria: existente, jaExistia: true } };

    const nova: DiariaLocal = {
      localId: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      tenantHash: h,
      cpf: String(dados.cpf ?? "").replace(/\D/g, ""),
      // Identidade do TOKEN tem precedência sobre o que a tela passou.
      idPrestador: idSessao > 0 ? idSessao : Number(dados.idPrestador) || 0,
      idAdmCliente: Number(dados.idAdmCliente) || 0,
      idAdmEmpresa: Number(dados.idAdmEmpresa) || 0,
      postoNome: dados.postoNome ?? "",
      dataCompetencia,
      dataLog: agoraSqlLocal(agora),
      hora: `${p(agora.getHours())}:${p(agora.getMinutes())}`,
      latitude: dados.latitude,
      longitude: dados.longitude,
      latitudeLotacao: dados.latitudeLotacao,
      longitudeLotacao: dados.longitudeLotacao,
      distancia: dados.distancia,
      expressao: dados.expressao,
      fotoUri: fotoPersistida,
      enviado: false,
      tentativas: 0,
    };
      return {
        lista: [...lista, nova],
        retorno: { diaria: nova, jaExistia: false },
      };
    },
  );
};

// ── Reconciliação com o servidor ──────────────────────────────────────────────

/**
 * Janela entre o 200 do T-ARC (enfileirado) e o INSERT do T-Consumer.
 *
 * Dentro dela, "o servidor não tem a diária" NÃO é prova de nada — é a mensagem ainda
 * na fila. 10 minutos (a mesma folga da cura do ponto do CLT) cobre o consumo normal
 * (segundos) e um acúmulo de pico.
 */
const JANELA_FILA_MS = 10 * 60 * 1000;

// Reenvio de DIÁRIA FANTASMA (o T-ARC aceitou e o banco não tem): no máximo 3 vezes por
// diária, com 6 h entre elas — mesmos tetos da cura do ponto do CLT. Reenviar é seguro:
// a UNIQUE pk_ponto (CPF + competência + tipo) no T-Consumer faz o 2º INSERT convergir.
const CURA_MAX = 3;
const CURA_INTERVALO_MS = 6 * 60 * 60 * 1000;

/** A diária já foi vista NO BANCO? (`enviadoEm === 1` é o carimbo antigo de confirmada.) */
const foiConfirmada = (d: DiariaLocal) =>
  Number(d.confirmadaEm) > 0 || Number(d.enviadoEm) === 1;

/**
 * Confere a diária do dia contra o SERVIDOR e corrige o espelho local.
 *
 * Existe porque o espelho é otimista: ele diz "registrada" no instante do toque e,
 * sem esta conferência, continuaria dizendo isso PARA SEMPRE — inclusive depois de
 * a linha ser excluída do banco (foi o que apareceu no teste de 2026-08-14).
 *
 * ⚠️ As três regras que evitam perder registro:
 *
 *  1. **Só age com resposta FRESCA do servidor.** Falha de rede/timeout não muda
 *     nada — offline, quem manda é o espelho. (Mesma regra do estado de documento:
 *     reverter só com confirmação fresca, nunca por ausência de resposta.)
 *  2. **Só remove diária JÁ ENVIADA.** Uma diária pendente é justamente a que o
 *     servidor AINDA não tem: apagá-la por "não existe lá" seria destruir o
 *     registro offline que estamos tentando subir.
 *  3. **Adota o que o servidor tem.** Diária registrada em outro aparelho (ou pelo
 *     legado/web) passa a aparecer aqui — e o botão bloqueia corretamente.
 */
export const reconciliarDiariaDoDia = async (
  dataCompetencia: string = competenciaDeHoje(),
): Promise<{ reconciliou: boolean; diaria: DiariaLocal | null }> => {
  const escopo = await escopoPrestador();
  const { hash: h } = await identidadePrestador();
  if (!escopo || !h) return { reconciliou: false, diaria: null };

  const geracao = peekPrestadorSessionGen();
  const resp = await ApiPrestador.diaria.consultarDia(dataCompetencia);

  // Sem resposta boa (offline, 503, erro) → NÃO mexe no espelho.
  if (!resp?.success) {
    return { reconciliou: false, diaria: await diariaDoDia(dataCompetencia) };
  }
  // Sessão trocou durante a consulta, ou a resposta é de outro tenant.
  if (peekPrestadorSessionGen() !== geracao) {
    return { reconciliou: false, diaria: null };
  }
  const hashResposta = normalizarHash(resp.done?.hash);
  if (hashResposta && hashResposta !== h) {
    return { reconciliou: false, diaria: null };
  }

  const registradaNoServidor = !!resp.done?.registrada;
  const horaServidor = String(resp.done?.hora ?? "").trim();
  const idAdmClienteServidor = Number(resp.done?.idAdmCliente) || 0;

  const diaria = await mutarDiarias<DiariaLocal | null>(escopo, (lista) => {
    const idx = lista.findIndex((d) => d.dataCompetencia === dataCompetencia);
    const local = idx >= 0 ? lista[idx] : null;

    // Servidor NÃO tem a diária.
    if (!registradaNoServidor) {
      // Pendente de envio: é esperado o servidor não ter. MANTÉM.
      if (!local || !local.enviado) return { lista, retorno: local };
      // Já esteve NO BANCO e não está mais → foi excluída no servidor. Remove do
      // espelho para o botão liberar de novo (e a foto local vai junto).
      if (foiConfirmada(local)) {
        const semEla = lista.filter((_, i) => i !== idx);
        void apagarFoto(local.fotoUri);
        return { lista: semEla, retorno: null };
      }
      // Enviada há POUCO: o 200 do T-ARC é "enfileirado", não "gravado". O
      // T-Consumer pode não ter inserido ainda. Espera a janela passar.
      const enviadaEm = Number(local.enviadoEm) || 0;
      if (enviadaEm > 0 && Date.now() - enviadaEm < JANELA_FILA_MS) {
        return { lista, retorno: local };
      }
      // Passou da janela e o banco NUNCA teve: DIÁRIA FANTASMA (o T-ARC enfileirou e o
      // T-Consumer não gravou). Antes ela era apagada aqui como se tivesse sido excluída
      // — e o registro se perdia. Agora volta a PENDENTE e o dreno reenvia (com o link
      // da foto, que já está no storage). Esgotados os tetos, fica como está: o
      // reprocessador do parking-lot do T-Consumer é a última rede.
      const curas = local.curas ?? 0;
      const ultimaCura = Number(local.ultimaCuraEm) || 0;
      if (
        curas >= CURA_MAX ||
        (ultimaCura > 0 && Date.now() - ultimaCura < CURA_INTERVALO_MS)
      ) {
        return { lista, retorno: local };
      }
      console.warn(
        `[PrestadorDiaria] diária ${local.dataCompetencia} enviada e ausente do banco — volta a pendente para reenvio (${curas + 1}/${CURA_MAX}).`,
      );
      const curada: DiariaLocal = {
        ...local,
        enviado: false,
        enviadoEm: undefined,
        curas: curas + 1,
        ultimaCuraEm: Date.now(),
      };
      const nova = [...lista];
      nova[idx] = curada;
      return { lista: nova, retorno: curada };
    }

    // Servidor TEM a diária.
    if (local) {
      // Marca como confirmada (está NO BANCO) e adota a hora do servidor, que é a
      // gravada de fato. A selfie local já não tem o que fazer aqui.
      if (local.fotoUri) void apagarFoto(local.fotoUri);
      const atualizada: DiariaLocal = {
        ...local,
        enviado: true,
        // Carimbo antigo de "confirmada pelo banco" (mantido para versões anteriores).
        enviadoEm: 1,
        confirmadaEm: Date.now(),
        hora: horaServidor || local.hora,
        fotoUri: null,
        ultimoErro: undefined,
        recusas: 0,
        proximaTentativaEm: undefined,
      };
      const nova = [...lista];
      nova[idx] = atualizada;
      return { lista: nova, retorno: atualizada };
    }

    // Nada local: a diária foi registrada em OUTRO aparelho (ou pelo sistema web).
    // Cria a entrada já confirmada para o botão refletir a realidade.
    const doServidor: DiariaLocal = {
      localId: `srv_${dataCompetencia.replace(/\D/g, "")}`,
      tenantHash: h,
      cpf: "",
      idPrestador: 0,
      idAdmCliente: idAdmClienteServidor,
      idAdmEmpresa: 0,
      postoNome: "",
      dataCompetencia,
      dataLog: "",
      hora: horaServidor,
      latitude: null,
      longitude: null,
      latitudeLotacao: null,
      longitudeLotacao: null,
      distancia: null,
      expressao: null,
      fotoUri: null,
      enviado: true,
      enviadoEm: 1, // veio do banco: nunca está "na fila"
      confirmadaEm: Date.now(),
      tentativas: 0,
    };
    return { lista: [...lista, doServidor], retorno: doServidor };
  });

  return { reconciliou: true, diaria };
};

// ── Dreno (envio em background) ───────────────────────────────────────────────

const TETO_TENTATIVAS_LOG = 8;

/**
 * Espera do envio AUTOMÁTICO depois de uma RECUSA do servidor (resposta da nossa API com
 * erro de negócio — 400/401/403/409/422). Repetir na hora dá o mesmo erro; com o
 * agendador e a tarefa em segundo plano, sem espera virava uma tentativa a cada poucos
 * minutos, o dia inteiro, com a selfie em base64 no corpo. Mesma régua da batida do CLT
 * (Sync.tsx: ESPERAS_PONTO_RECUSADO_MS). Rede, timeout e 5xx não contam; puxar a tela
 * ignora a espera.
 */
const ESPERAS_RECUSA_MS = [
  2 * 60_000,
  10 * 60_000,
  30 * 60_000,
  2 * 60 * 60_000,
];

const emEspera = (d: DiariaLocal, agora: number) =>
  (Number(d.proximaTentativaEm) || 0) > agora;

/** Diária de OUTRA sessão (tenant ou pessoa) — nunca sai por esta. */
const deOutraSessao = (d: DiariaLocal, hash: string, idSessao: number) =>
  normalizarHash(d.tenantHash) !== hash ||
  (idSessao > 0 &&
    Number(d.idPrestador) > 0 &&
    Number(d.idPrestador) !== idSessao);

/** Pendências de envio do tenant — usado para avisar antes de sair. */
export const contarDiariasPendentes = async (): Promise<number> => {
  const lista = await lerDiariasLocais();
  return lista.filter((d) => !d.enviado).length;
};

/**
 * Há diária DESTA sessão que pode sair AGORA (pendente e fora da espera de recusa)?
 * Só leitura local — é o que o agendador e a tarefa em segundo plano consultam antes de
 * gastar rede.
 */
export const temDiariaParaEnviar = async (): Promise<boolean> => {
  try {
    const { hash, idPrestador } = await identidadePrestador();
    if (!hash) return false;
    const agora = Date.now();
    return (await lerDiariasLocais()).some(
      (d) =>
        !d.enviado &&
        !emEspera(d, agora) &&
        !deOutraSessao(d, hash, idPrestador),
    );
  } catch {
    return false;
  }
};

export type ResultadoDreno = {
  enviadas: number;
  pendentes: number;
  retidasOutroTenant: number;
  /** URL pública da selfie de uma diária enviada agora (para adotar como perfil). */
  fotoUrl: string | null;
};

/**
 * UM dreno por vez (por sessão). A câmera, a tela e o agendador disparavam juntos e a
 * mesma diária subia duas vezes — a selfie em base64 duas vezes no storage. Quem chega
 * com um dreno em curso recebe o MESMO resultado: a tela, que confere com o servidor
 * depois do dreno, espera o envio de verdade terminar. Um pedido MANUAL (puxar a tela)
 * que encontra um automático em curso roda de novo depois dele, já sem a espera de recusa.
 */
let drenoEmCurso: {
  geracao: number;
  manual: boolean;
  promessa: Promise<ResultadoDreno>;
} | null = null;

/**
 * Envia as diárias pendentes DO TENANT DA SESSÃO ATUAL.
 *
 * Diária de outro tenant é RETIDA (nunca descartada): ela sobe quando o prestador
 * voltar àquela empresa. Enviá-la agora gravaria no banco errado.
 *
 * Nada aqui apaga uma diária: só marca `enviado`. Falha de rede, 4xx, timeout —
 * tudo continua no espelho para a próxima rodada.
 *
 * @param opcoes.manual `true` quando a pessoa PUXOU a tela: ignora a espera de recusa.
 */
export const drenarDiarias = (opcoes?: {
  manual?: boolean;
}): Promise<ResultadoDreno> => {
  const manual = !!opcoes?.manual;
  const geracao = peekPrestadorSessionGen();
  const emCurso = drenoEmCurso;
  if (emCurso && emCurso.geracao === geracao) {
    if (emCurso.manual || !manual) return emCurso.promessa;
    return emCurso.promessa
      .catch(() => undefined)
      .then(() => drenarDiarias({ manual: true }));
  }
  const promessa = _drenarDiarias(manual).finally(() => {
    if (drenoEmCurso?.promessa === promessa) drenoEmCurso = null;
  });
  drenoEmCurso = { geracao, manual, promessa };
  return promessa;
};

const _drenarDiarias = async (manual: boolean): Promise<ResultadoDreno> => {
  const vazio: ResultadoDreno = {
    enviadas: 0,
    pendentes: 0,
    retidasOutroTenant: 0,
    fotoUrl: null,
  };
  const escopo = await escopoPrestador();
  const { hash, idPrestador: idSessao } = await identidadePrestador();
  if (!escopo || !hash) return vazio;

  const agora = Date.now();
  const lista = await lerDiariasLocais();
  const pendentes = lista.filter(
    (d) => !d.enviado && (manual || !emEspera(d, agora)),
  );
  let enviadas = 0;
  let retidas = 0;
  let fotoUrl: string | null = null;

  if (pendentes.length > 0) {
    // POR ONDE enviar. A NOSSA API responde? ("qualquer internet" não serve: o Wi-Fi de
    // órgão público abre o Google e bloqueia os nossos hosts — relato de 30/09/2026.)
    // Se não, e a rede atual é Wi-Fi, tenta pelos DADOS MÓVEIS (Android). Sem nenhum dos
    // dois, nem tenta: cada envio esperaria 45 s para falhar.
    let viaDadosMoveis = false;
    if (!(await SyncService.apiAlcancavel())) {
      const celular =
        DadosMoveis.disponivel &&
        (await DadosMoveis.redeAtualEhWifi()) &&
        (await Api.ponto.alcancavelPorDadosMoveis());
      if (!celular) {
        return { ...vazio, pendentes: lista.filter((d) => !d.enviado).length };
      }
      viaDadosMoveis = true;
    }

    for (const diaria of pendentes) {
      // Guarda de sessão: o espelho já é por tenant+pessoa, mas uma diária carimbada
      // com OUTRO tenant ou OUTRO prestador (migração, bug futuro) NUNCA pode ser
      // enviada nesta sessão — iria para o banco errado ou no nome de outra pessoa.
      // Retida, nunca descartada: sobe quando aquela sessão voltar.
      if (deOutraSessao(diaria, hash, idSessao)) {
        retidas += 1;
        continue;
      }

      const geracao = peekPrestadorSessionGen();

      // A foto: o LINK, se a selfie já subiu num envio anterior (reenvio de diária
      // fantasma — o arquivo local é apagado depois do envio, e o T-ARC repassa link sem
      // subir de novo); senão a base64, que o T-ARC sobe para o storage (mesmo contrato
      // do ponto do CLT).
      let foto: string | null = null;
      if (diaria.fotoUrl && /^https?:\/\//i.test(diaria.fotoUrl)) {
        foto = diaria.fotoUrl;
      } else if (diaria.fotoUri) {
        try {
          // Caminho de antes de uma atualização (iOS muda a pasta do app) → pasta atual.
          foto = await FileSystem.readAsStringAsync(
            caminhoNoAppAtual(diaria.fotoUri),
            { encoding: FileSystem.EncodingType.Base64 },
          );
        } catch {
          // Foto ilegível (arquivo sumiu): manda a diária SEM foto. O registro vale
          // mais que a selfie — perder a diária seria pior.
          foto = null;
        }
      }

      const payload = {
        hash,
        cpf: diaria.cpf,
        // Nome do campo IGUAL ao do modelo C# (o binder do .NET casa por nome): é o
        // id que entra no CAMINHO e no NOME do arquivo da selfie
        // (.../{dia}/{idFreelancer}/F_{idFreelancer}_...). `idPrestador` sozinho não
        // casaria com nada e a foto cairia na pasta do CPF.
        id_adm_colaborador_freelancer: diaria.idPrestador,
        id_adm_cliente: diaria.idAdmCliente,
        id_adm_empresa: diaria.idAdmEmpresa,
        data_log: diaria.dataLog,
        data_competencia: diaria.dataCompetencia,
        latitude: diaria.latitude,
        longitude: diaria.longitude,
        latitude_ponto: diaria.latitude,
        longitude_ponto: diaria.longitude,
        latitude_lotacao: diaria.latitudeLotacao,
        longitude_lotacao: diaria.longitudeLotacao,
        distancia: diaria.distancia,
        expressao: diaria.expressao,
        foto,
      };
      const resp = viaDadosMoveis
        ? await ApiPrestador.diaria.registrarPorDadosMoveis(payload)
        : await ApiPrestador.diaria.registrar(payload);

      // Sessão trocou durante o envio: para o dreno aqui. O que já foi confirmado
      // está marcado; o resto tenta na próxima rodada, no tenant certo.
      if (peekPrestadorSessionGen() !== geracao) break;

      // "Enviada" só com o RECIBO do T-ARC (`{ enfileirado: true }`). Um 2xx sem ele não
      // veio da nossa API — marcar "enviada" ali era a diária que nunca saiu do aparelho.
      if (resp?.success && resp.done?.enfileirado === true) {
        // URL pública devolvida pelo T-ARC (o storage já recebeu a imagem). É o que o
        // app adota como foto de perfil quando o prestador ainda não tem uma — e o que
        // um eventual reenvio manda no lugar da base64.
        const url = String(resp.done?.foto ?? "").trim();
        const urlValida =
          url.startsWith("http") && !url.includes("sem_foto") ? url : "";
        if (urlValida) fotoUrl = urlValida;
        // A selfie local só sai quando o storage confirmou a cópia (URL de volta). Sem
        // URL, ela fica até a diária ser vista no banco (reconciliarDiariaDoDia).
        const apagarLocal = !!urlValida && !!diaria.fotoUri;
        await mutarDiarias(escopo, (atual) => ({
          lista: atual.map((d) =>
            d.localId === diaria.localId
              ? {
                  ...d,
                  enviado: true,
                  enviadoEm: Date.now(),
                  fotoUrl: urlValida || d.fotoUrl,
                  fotoUri: apagarLocal ? null : d.fotoUri,
                  ultimoErro: undefined,
                  recusas: 0,
                  proximaTentativaEm: undefined,
                }
              : d,
          ),
          retorno: null,
        }));
        if (apagarLocal) await apagarFoto(diaria.fotoUri);
        enviadas += 1;
        continue;
      }

      // Falha. Sem resposta, página no lugar da API, 5xx ou 2xx sem recibo = REDE (tenta
      // de novo na próxima rodada). Erro de negócio da nossa API = RECUSA: espera a vez.
      const recusa = !resp?.success && resp?.offline !== true;
      const erro =
        (resp?.erros ?? []).join("; ") ||
        (resp?.success ? "resposta sem recibo do servidor" : "falha ao enviar");
      await mutarDiarias(escopo, (atual) => ({
        lista: atual.map((d) => {
          if (d.localId !== diaria.localId) return d;
          const recusas = recusa ? (d.recusas ?? 0) + 1 : (d.recusas ?? 0);
          return {
            ...d,
            tentativas: (d.tentativas ?? 0) + 1,
            ultimoErro: erro.slice(0, 200),
            ...(recusa
              ? {
                  recusas,
                  proximaTentativaEm:
                    Date.now() +
                    ESPERAS_RECUSA_MS[
                      Math.min(recusas - 1, ESPERAS_RECUSA_MS.length - 1)
                    ],
                }
              : {}),
          };
        }),
        retorno: null,
      }));
      if ((diaria.tentativas ?? 0) + 1 === TETO_TENTATIVAS_LOG) {
        // Sinaliza no console para diagnóstico; a diária CONTINUA na fila.
        console.warn(
          `[PrestadorDiaria] diária ${diaria.dataCompetencia} falhou ${TETO_TENTATIVAS_LOG}× — último erro: ${erro}`,
        );
      }
      // Pelos dados móveis sem resposta: não insiste diária a diária nesta rodada.
      if (viaDadosMoveis && !recusa) break;
    }
  }

  const restantes = (await lerDiariasLocais()).filter((d) => !d.enviado);
  return {
    enviadas,
    pendentes: restantes.length,
    retidasOutroTenant: retidas,
    fotoUrl,
  };
};
